import cc from "classcat"
import Checkmark from "images/icon-checkmark-circle.svg"
import kebabCase from "lodash.kebabcase"
import H2 from "components/atoms/H2"

const Stats = ({ stats }) => {
  return (
    <div className="overflow-hidden bg-white">
      <div className="max-w-2xl justify-between gap-6 space-y-6 sm:flex sm:space-y-0 lg:mx-auto">
        {stats.map((f) => (
          <div className="flex max-w-xl flex-col border-l-8 border-kkm-green pl-3 lg:mx-0 lg:max-w-none">
            <div className="font-display text-2xl leading-7 text-zinc-600">
              {f.line1}
            </div>
            <div className="text-base text-zinc-800">{f.line2}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

const ListItem = ({ item }) => {
  return (
    <li className="flex items-start">
      <div className="shrink-0 pt-1">
        <Checkmark className="h-6 w-6 transform text-kkm-darkgreen" />
      </div>
      <p className="ml-3 text-base leading-6 text-zinc-800">{item}</p>
    </li>
  )
}

const Feature = ({
  title,
  description,
  Image,
  alternate = false,
  imageMask = false,
}) => {
  return (
    <div className="relative">
      <div className="lg:grid lg:grid-flow-col-dense lg:grid-cols-2 lg:gap-24">
        <div
          className={cc([
            "max-w-2xl",
            alternate ? "lg:col-start-2" : "lg:col-start-1",
          ])}
        >
          <div className="space-y-4">
            {title ? (
              <h2 className="text-2xl font-extrabold tracking-tight text-zinc-900">
                {title}
              </h2>
            ) : null}
            {Array.isArray(description) ? (
              <ul className="space-y-2">
                {description.map((feature) => (
                  <ListItem item={feature} key={kebabCase(feature)} />
                ))}
              </ul>
            ) : (
              <div className="text-lg text-zinc-800">{description}</div>
            )}
          </div>
        </div>
        <div
          className={cc([
            "mt-12 max-w-2xl lg:relative lg:mx-0 lg:mt-0 lg:max-w-none",
            alternate ? "lg:col-start-1 lg:text-right" : "lg:col-start-2",
          ])}
        >
          <div
            className={cc([
              "relative z-[1] flex h-full overflow-hidden",
              {
                "rounded-r-full": imageMask && alternate,
                "rounded-l-full": imageMask && !alternate,
              },
            ])}
          >
            <Image />
          </div>
        </div>
      </div>
    </div>
  )
}

export default function AlternatingFeatures({
  features,
  title = "",
  imageMask = false,
  theme = "light",
  titleAlign,
}) {
  const isOdd = (number) => number % 2 === 1
  return (
    <div
      className={cc([
        "relative py-16 lg:py-24",
        {
          "bg-white": theme === "light",
          "bg-kkm-green": theme === "green",
          "bg-kkm-yellow": theme === "yellow",
        },
      ])}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {title ? (
          <H2
            className="m-0 mb-12 whitespace-pre-line text-2xl tracking-tight text-neutral-800 sm:text-4xl"
            textAlign={titleAlign}
          >
            {title}
          </H2>
        ) : null}
        <div className="space-y-20">
          {features.map((feature, index) => (
            <>
              <Feature
                {...feature}
                alternate={!isOdd(index)}
                imageMask={imageMask}
              />
              {feature.stats && <Stats stats={feature.stats} />}
            </>
          ))}
        </div>
      </div>
    </div>
  )
}
